import { Trans } from "@lingui/macro";
import { Box, Typography } from "@mui/material";
import {
  FancyIcon,
  WfActionGroup,
  WfButton,
  WfIconContentErrorRegular,
} from "@WahooFitness/redesignr";
import { useState } from "react";

type CancelConfirmationContentProps = {
  tierLabel: string;
  downgradeTierLabel: string;
  accessUntilLabel?: string;
  onKeepPlan: () => void;
  onCancelPlan: () => Promise<unknown>;
};

const CancelConfirmationContent = ({
  tierLabel,
  downgradeTierLabel,
  accessUntilLabel,
  onKeepPlan,
  onCancelPlan,
}: CancelConfirmationContentProps) => {
  const [isCanceling, setIsCanceling] = useState(false);

  const handleCancelClick = async () => {
    if (isCanceling) return;
    setIsCanceling(true);
    try {
      await onCancelPlan();
    } finally {
      setIsCanceling(false);
    }
  };

  return (
    <Box display="flex" flexDirection="column">
      <Box display="flex" flexDirection="column" alignItems="center" gap={2} p={2}>
        <FancyIcon icon={<WfIconContentErrorRegular />} color="error" size="medium" />
        <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
          <Typography variant="prose-lg-medium" textAlign="center">
            <Trans>Cancel your subscription?</Trans>
          </Typography>
          <Typography variant="prose-base" color="text.secondary" textAlign="center">
            {accessUntilLabel ? (
              <Trans>
                You&apos;ll keep access to {tierLabel} until {accessUntilLabel}. After that, your
                account will downgrade to the {downgradeTierLabel} plan.
              </Trans>
            ) : (
              <Trans>
                You&apos;ll lose access to {tierLabel} and downgrade to the {downgradeTierLabel}{" "}
                plan as soon as your plan is canceled.
              </Trans>
            )}
          </Typography>
        </Box>
      </Box>
      <WfActionGroup orientation="vertical" fullWidth>
        <WfButton
          hierarchy="tertiary"
          intent="default"
          size="large"
          fullWidth
          disabled={isCanceling}
          onClick={onKeepPlan}
        >
          <Trans>Keep plan</Trans>
        </WfButton>
        <WfButton
          hierarchy="tertiary"
          intent="destructive"
          size="large"
          fullWidth
          disabled={isCanceling}
          loading={isCanceling}
          onClick={handleCancelClick}
        >
          <Trans>Cancel plan</Trans>
        </WfButton>
      </WfActionGroup>
    </Box>
  );
};

export default CancelConfirmationContent;
